import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Stack,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BranchIcon from "@mui/icons-material/AccountTree";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import { OverAllLoader } from "../../components/OverAllLoader";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import { TruncatedText } from "../../components/TruncatedText";
import CustomPagination from "../../components/CustomPagination";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import EditIcon from '@mui/icons-material/Edit';
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const ViewBatch = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [batchData, setBatchData] = useState([]);
  const location = useLocation();
  const { BatchId } = location.state || "";
  const navigate = useNavigate();

  // Students table state
  const [studentsListData, setStudentsListData] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsCount, setStudentsCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearch = useDebounce(searchKey);
  const [showTable, setShowTable] = useState(false);

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("GetBatchById", {
        batch_id: BatchId,
      });
      setBatchData(res?.data?.data);
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching batch by ID:", error);
      setOverAllLoading(false);
    }
  };

  const fetchStudentsByBatch = async () => {
    setStudentsLoading(true);
    try {
      const payload = {
        page: page,
        size: rowsPerPage,
        search_key: debouncedSearch,
        batch_id: BatchId,
      };
      const res = await api.post("ListStudents", payload);
      if (res?.data?.status === "success") {
        setStudentsListData(res?.data?.data);
        setStudentsCount(res?.data?.count);
      }
      setStudentsLoading(false);
    } catch (error) {
      console.error("Error fetching students by batch:", error);
      setStudentsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setPage(0);
    setSearchKey(e.target.value);
  };

  useEffect(() => {
    fetchById();
  }, []);

  useEffect(() => {
    fetchStudentsByBatch();
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Fetching batch details..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* View Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: "#1c97e3",
                    p: 0.5,
                    borderRadius: "10px",
                  }}
                >
                  <BranchIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Grid>
                  <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                    {getLabel('BATCHES')} Details
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Batch Details */}
            <Box
              sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
            >
              {/* Basic Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BranchIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {getLabel('BATCHES')}  Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  width: "100%",
                }}
              >
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">
                      {getLabel('BATCHES')} Name
                    </Typography>
                    <Typography>{batchData.batch_name}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Typography>{batchData.status}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created By</Typography>
                    <Typography>{batchData.created_by_name}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created Date</Typography>
                    <Typography>
                      {dayjs(batchData.created_at).format("DD-MM-YYYY")}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated By</Typography>
                    <Typography>{batchData.updated_by_name || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated Date</Typography>
                    <Typography>
                      {batchData.updated_at
                        ? dayjs(batchData.updated_at).format("DD-MM-YYYY")
                        : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Institute Info */}
              {localStorage.getItem("role") === "SUPER_ADMIN" && (
                <>
                  <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BusinessIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                    <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                      {`${getLabel('INSTITUTE')} Information`}
                    </Typography>
                  </Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      rowGap: 2,
                    }}
                  >
                    <Grid sx={{ display: "flex" }}>
                      <Grid item xs={6} sx={{ width: "50%" }}>
                        <Typography fontWeight="600">{`${getLabel('INSTITUTE')} Name`}</Typography>
                        <Typography>{batchData.institute_name}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Box>

          {/* Students Table Section */}
          <Box sx={{ mt: 4 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 0.5,
                  alignItems: "center",
                }}
              >
                <Box className="icon-box">
                  <GroupIcon className="iconHeading" />
                </Box>
                <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
                  {getLabel('STUDENT')} in {getLabel('BATCHES')}
                </Typography>
              </Grid>
              <Box display="flex" gap={4} alignItems="center">
                <TextField
                  value={searchKey}
                  onChange={(e) => handleSearch(e)}
                  size="small"
                  variant="outlined"
                  placeholder={`Search ${getLabel('STUDENT')}`}
                  className="custom-search-field"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                mt: 3,
                opacity: showTable ? 1 : 0,
                transform: showTable ? "translateY(0)" : "translateY(40px)",
                transition: "opacity 0.5s ease, transform 0.5s ease",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow className="tableHeader">
                    <TableCell className="tableHeader">
                      <b>S.No</b>
                    </TableCell>
                    <TableCell className="tableHeader">
                      <b>Name</b>
                    </TableCell>
                    <TableCell className="tableHeader">
                      <b>Contact</b>
                    </TableCell>
                    {localStorage.getItem("role") === "SUPER_ADMIN" && (
                      <TableCell className="tableHeader">
                        <b>{`${getLabel('INSTITUTE')}`}</b>
                      </TableCell>
                    )}
                    {localStorage.getItem("role") !== "TRAINER" && (
                      <TableCell className="tableHeader">
                        <b>Assigned {getLabel('TRAINER')}</b>
                      </TableCell>
                    )}
                    <TableCell className="tableHeader">
                      <b>Created Date</b>
                    </TableCell>
                    <TableCell className="tableHeader">
                      <b>Created By</b>
                    </TableCell>
                    <TableCell className="tableHeader">
                      <b>Status</b>
                    </TableCell>
                    <TableCell className="tableHeader">
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {studentsLoading ? (
                  <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                    <TableLoader message="Fetching students..." />
                  </TableCell>
                ) : (
                  <TableBody className="tablebody">
                    {studentsListData.map((item, index) => (
                      <TableRow
                        key={item.user_id}
                        hover
                        sx={{
                          backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                          "&:hover": { backgroundColor: "#f1f1f1" },
                        }}
                      >
                        <TableCell align="center">
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>
                          <TruncatedText
                            text={item.first_name + " " + item.last_name}
                            maxWidth={160}
                            sx={{ fontWeight: "bold" }}
                          />
                          {item?.gender}
                        </TableCell>
                        <TableCell>
                          {item?.phone}
                          <br />
                          <TruncatedText text={item?.email} maxWidth={170} />
                        </TableCell>
                        {localStorage.getItem("role") === "SUPER_ADMIN" && (
                          <TableCell>
                            <TruncatedText text={item.institute_name} maxWidth={160} />
                          </TableCell>
                        )}
                        {localStorage.getItem("role") !== "TRAINER" && (
                          <TableCell
                            sx={{
                              cursor: item.trainer_names?.length
                                ? "pointer"
                                : "default",
                              verticalAlign: "middle",
                              textAlign: "center",
                            }}
                          >
                            {item.trainer_names && item.trainer_names.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Box sx={{ display: "flex" }}>
                                  {item.trainer_names.slice(0, 4).map((name, index) => (
                                    <Tooltip
                                      key={index}
                                      arrow
                                      title={
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          sx={{
                                            background:
                                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            borderRadius: 2,
                                            p: "6px 10px",
                                            boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                                          }}
                                        >
                                          <Avatar
                                            sx={{
                                              width: 28,
                                              height: 28,
                                              fontSize: 13,
                                              background:
                                                "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                                              color: "#fff",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {name.charAt(0).toUpperCase()}
                                          </Avatar>
                                          <Typography
                                            sx={{
                                              color: "#fff",
                                              fontSize: 13,
                                              fontWeight: 500,
                                              textShadow:
                                                "0px 1px 2px rgba(0,0,0,0.4)",
                                            }}
                                          >
                                            {name}
                                          </Typography>
                                        </Stack>
                                      }
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            background: "transparent",
                                            p: 0,
                                          },
                                        },
                                        arrow: {
                                          sx: { color: "transparent" },
                                        },
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          fontSize: 13,
                                          background:
                                            "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                                          color: "#fff",
                                          fontWeight: 600,
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                          cursor: "pointer",
                                          transition: "transform 0.2s ease",
                                          "&:hover": {
                                            transform: "scale(1.1)",
                                            zIndex: 5,
                                          },
                                          ml: index === 0 ? 0 : -1.0,
                                          border: "2px solid #fff",
                                        }}
                                      >
                                        {name.charAt(0).toUpperCase()}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                  {item.trainer_names.length > 4 && (
                                    <Tooltip
                                      arrow
                                      title={item.trainer_names.slice(4).join(", ")}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 30,
                                          height: 30,
                                          fontSize: 12,
                                          backgroundColor: "#e2e8f0",
                                          color: "#334155",
                                          fontWeight: 600,
                                          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                          cursor: "pointer",
                                          ml: -1.0,
                                          border: "2px solid #fff",
                                        }}
                                      >
                                        +{item.trainer_names.length - 4}
                                      </Avatar>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#888",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Not Assigned Yet
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ textWrap: "nowrap" }}>
                          {item?.created_at
                            ? dayjs(item.created_at).format("DD-MM-YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <TruncatedText
                            text={item?.created_by_name || item?.created_by || "-"}
                            maxWidth={140}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 2,
                              py: 0.5,
                              borderRadius: "12px",
                              fontWeight: "bold",
                              fontSize: "12px",
                              backgroundColor:
                                item.status === "ACTIVE"
                                  ? "#d4edda"
                                  : item.status === "INACTIVE"
                                    ? "#f8d7da"
                                    : "#f1f1f1",
                              color:
                                item.status === "ACTIVE"
                                  ? "#155724"
                                  : item.status === "INACTIVE"
                                    ? "#721c24"
                                    : "#333",
                              textAlign: "center",
                            }}
                          >
                            {item.status}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            display="flex"
                            justifyContent="left"
                            alignItems="center"
                            gap={0.5}
                          >
                            <Tooltip title="View" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigate("/viewstudent", {
                                  state: {
                                    addStudent: "false",
                                    StudentId: item?.user_id,
                                  },
                                });
                              }}
                              sx={{
                                display: "flex",
                                gap: 0.5,
                              }}
                            >
                              <VisibilityIcon color="primary" />
                            </IconButton>
                            </Tooltip>
                            <Tooltip title={`${getLabel('EXAM')} History`} placement="top">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigate("/examHistory", {
                                  state: {
                                    user_Id: item?.user_id,
                                    dashboard: true,
                                    studentName:
                                      item?.first_name + " " + item?.last_name,
                                    viewAssessments: true,
                                    studentid: item?.user_id,
                                  },
                                });
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <HistoryIcon color="primary" />
                            </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                    {((page === 0 && studentsListData?.length > rowsPerPage - 1) ||
                      page !== 0) && (
                        <CustomPagination
                          page={page}
                          rowsPerPage={rowsPerPage}
                          count={studentsCount}
                          setPage={setPage}
                          colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8}
                        />
                      )}

                    {studentsListData?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={localStorage.getItem("role") === "SUPER_ADMIN" ? 9 : localStorage.getItem("role") === "TRAINER" ? 7 : 8} align="center">
                          <TableNodata />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ViewBatch;
